import { SurveyCustomerExperienceOptions } from '@airship-platform/airship-patterns';
import { FlexBox, ItemSelector } from '@airship-platform/jam-web/components';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { AppointmentSurvey } from '../../../../data-access/appointmentProposals';

type CustomerExperienceSectionProps = {
  options: {
    current: SurveyCustomerExperienceOptions[];
    priorities: SurveyCustomerExperienceOptions[];
  };
  defaultValues: AppointmentSurvey['customerExperience'];
  formId?: string;
  onSubmit: (data: AppointmentSurvey['customerExperience']) => Promise<void>;
};

const CustomerExperienceSchema = z.object({
  current: z.string().array(),
  priorities: z.string().array(),
});

export type CustomerExperienceInputs = AppointmentSurvey['customerExperience'];

const CustomerExperienceSection: React.FC<CustomerExperienceSectionProps> = ({
  options,
  defaultValues,
  formId,
  onSubmit,
}) => {
  const { control, handleSubmit } = useForm<CustomerExperienceInputs>({
    resolver: zodResolver(CustomerExperienceSchema),
    defaultValues,
  });

  const onChange = (fieldValue: string[], value: string) => {
    const updated = fieldValue.includes(value)
      ? fieldValue.filter((item) => item !== value)
      : [...fieldValue, value];
    return updated;
  };

  return (
    <Box
      component={'form'}
      id={formId}
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="off"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        width: '100%',
      }}
    >
      <Controller
        name="current"
        control={control}
        render={({ field }) => (
          <FlexBox flexDirection="column" gap={2}>
            <Typography variant="subtitle2">Current Experience</Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 2,
              }}
            >
              {options.current.map((option) => (
                <ItemSelector
                  key={option.label}
                  label={option.label}
                  value={option.label}
                  startIcon={option.icon}
                  checked={field.value.includes(option.label)}
                  type="checkbox"
                  handleChange={() =>
                    field.onChange(onChange(field.value || [], option.label))
                  }
                  fullWidth
                />
              ))}
            </Box>
          </FlexBox>
        )}
      />
      <Controller
        name="priorities"
        control={control}
        render={({ field }) => (
          <FlexBox flexDirection="column" gap={2}>
            <Typography variant="subtitle2">Priorities</Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 2,
              }}
            >
              {options.priorities.map((option) => (
                <ItemSelector
                  key={option.label}
                  label={option.label}
                  value={option.label}
                  startIcon={option.icon}
                  checked={field.value.includes(option.label)}
                  type="checkbox"
                  handleChange={() =>
                    field.onChange(onChange(field.value || [], option.label))
                  }
                  fullWidth
                />
              ))}
            </Box>
          </FlexBox>
        )}
      />
    </Box>
  );
};

export default CustomerExperienceSection;
